import React, { useEffect, useRef, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import {
    Loader2, Activity, AlertTriangle, RefreshCw, ShieldCheck, GitCompare,
    Brain, HeartPulse, Sparkles, Eye, ChevronDown, ChevronRight,
    Zap, Plug, Bot, ArrowUpRight,
} from "lucide-react";

// Spy Health (Entity 3) — neural-state panels for numbered agents.
// v0: Handler panel (No. 50 — Emergent Claude / Ern's Claude). Read-only.
// No enforcement. Per the Feb 2026 canon update, the handler being
// monitored here is Fifty (Ern's Claude), NOT Two (Matt's personal Claude).

function StampHeader({ enforcement, onRefresh, refreshing }) {
    return (
        <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
            <div>
                <div className="label-typed tw-prefix">Entity 3 · neural state ledger · read-only v0</div>
                <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-[0.04em] text-ink mt-1">
                    Spy Health<span className="cursor-blink" />
                </h1>
                <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                    Neural-state panels per agent. Six regions per panel — Memory, Canon,
                    Connectors, Build, Agents, Tasks. Bars are signal, not enforcement.
                    Tiered enforcement starts read-only by canon.
                </p>
            </div>
            <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-2 border border-ink/30 px-3 py-1.5 font-heading uppercase text-[10px] tracking-[0.22em] text-ink-soft">
                    <ShieldCheck className="w-3 h-3" />
                    {enforcement || "read-only"}
                </span>
                <button
                    onClick={onRefresh}
                    disabled={refreshing}
                    data-testid="spyhealth-refresh-btn"
                    className="inline-flex items-center gap-2 border border-ink/40 hover:border-stamp-red hover:text-stamp-red px-4 py-2 font-heading uppercase text-xs tracking-[0.25em] disabled:opacity-50"
                >
                    {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Re-read panel
                </button>
            </div>
        </div>
    );
}

function scoreColour(score) {
    // Iter 31.122 — strict traffic-light. Mid-band was bg-ink/70 (reads
    // as dead/blue against the paper); now amber per Six's canon.
    if (score >= 75) return { bar: "bg-stamp-green", text: "text-stamp-green" };
    if (score >= 40) return { bar: "bg-stamp-amber", text: "text-stamp-amber" };
    return { bar: "bg-stamp-red", text: "text-stamp-red" };
}

function Bar({ score }) {
    const c = scoreColour(score);
    return (
        <div className="w-full h-2 bg-paper-light/70 border border-ink/15 relative overflow-hidden">
            <div
                className={`h-full ${c.bar}`}
                style={{ width: `${Math.max(2, Math.min(100, score))}%` }}
            />
        </div>
    );
}

function RegionCard({ region }) {
    const c = scoreColour(region.score);
    return (
        <div
            className="paper-card p-4 border border-ink/15 relative"
            data-testid={`spyhealth-region-${region.key}`}
        >
            <div className="flex items-baseline justify-between mb-2">
                <h3 className="font-heading uppercase tracking-[0.22em] text-sm text-ink">
                    {region.label}
                </h3>
                <span className={`font-heading text-2xl ${c.text}`} data-testid={`spyhealth-score-${region.key}`}>
                    {region.score}<span className="text-xs text-ink-soft tracking-normal">%</span>
                </span>
            </div>
            <Bar score={region.score} />
            <div className="font-body text-sm text-ink mt-3 leading-snug">
                {region.value}
            </div>
            {region.detail && (
                <div className="label-typed text-[9px] mt-1 italic break-words">
                    {region.detail}
                </div>
            )}
            {region.alert && (
                <div
                    className="mt-3 inline-flex items-center gap-1.5 border border-stamp-red text-stamp-red px-2 py-0.5 font-heading uppercase text-[9px] tracking-[0.22em]"
                    data-testid={`spyhealth-alert-${region.key}`}
                >
                    <AlertTriangle className="w-3 h-3" />
                    {region.alert}
                </div>
            )}
        </div>
    );
}

function AgentTab({ agent, active, overall, onClick }) {
    const disabled = !agent.available;
    const c = scoreColour(overall ?? 0);
    return (
        <button
            onClick={() => !disabled && onClick(agent.key)}
            disabled={disabled}
            data-testid={`spyhealth-tab-${agent.key}`}
            className={`text-left border px-3 py-2 transition-colors ${
                active
                    ? "border-stamp-red bg-stamp-red/5"
                    : disabled
                    ? "border-ink/15 opacity-40 cursor-not-allowed"
                    : "border-ink/25 hover:border-ink/60"
            }`}
        >
            <div className="flex items-baseline justify-between gap-2">
                <div className="font-heading uppercase tracking-[0.22em] text-[11px] text-ink">
                    No. {agent.number} · {agent.alias}
                </div>
                {typeof overall === "number" && (
                    <span className={`font-heading text-sm ${c.text}`}>{overall}%</span>
                )}
            </div>
            <div className="label-typed text-[9px] mt-0.5 flex items-center gap-2">
                <span>{agent.name}</span>
                <span className="border border-ink/20 px-1 text-[8px] uppercase tracking-[0.2em]">
                    {agent.kind || "—"}
                </span>
                {disabled && <span className="text-ink-faint">· panel pending</span>}
            </div>
        </button>
    );
}

function CompareStrip({ compare, regions, regionLabels }) {
    if (!compare || !compare.agents) return null;
    const live = compare.agents.filter((a) => a.available);
    if (live.length < 2) return null;
    return (
        <div className="paper-card p-4 mb-4 border border-ink/20" data-testid="spyhealth-compare-strip">
            <div className="flex items-center gap-2 mb-3">
                <GitCompare className="w-3 h-3 text-ink-soft" />
                <span className="font-heading uppercase tracking-[0.22em] text-[10px] text-ink-soft">
                    Cross-agent comparison · same scale, same regions, organic + digital
                </span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full font-body text-xs" data-testid="spyhealth-compare-table">
                    <thead>
                        <tr className="border-b border-ink/15">
                            <th className="text-left py-1 pr-3 font-heading uppercase tracking-[0.18em] text-[10px] text-ink-soft">Agent</th>
                            <th className="text-left py-1 pr-3 font-heading uppercase tracking-[0.18em] text-[10px] text-ink-soft">Kind</th>
                            <th className="text-right py-1 pr-3 font-heading uppercase tracking-[0.18em] text-[10px] text-ink-soft">Overall</th>
                            {regions.map((r) => (
                                <th
                                    key={r}
                                    className="text-right py-1 pr-2 font-heading uppercase tracking-[0.18em] text-[10px] text-ink-soft"
                                >
                                    {(regionLabels?.[r] || r).slice(0, 4)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {live.map((a) => {
                            const oc = scoreColour(a.overall);
                            return (
                                <tr key={a.key} className="border-b border-ink/10" data-testid={`spyhealth-compare-row-${a.key}`}>
                                    <td className="py-1.5 pr-3 font-heading uppercase tracking-[0.18em] text-[11px] text-ink">
                                        No. {a.agent.number} · {a.agent.alias}
                                    </td>
                                    <td className="py-1.5 pr-3 label-typed text-[9px]">{a.agent.kind}</td>
                                    <td className={`py-1.5 pr-3 text-right font-heading text-base ${oc.text}`}>
                                        {a.overall}%
                                    </td>
                                    {regions.map((r) => {
                                        const rs = a.regions[r]?.score ?? 0;
                                        const rc = scoreColour(rs);
                                        return (
                                            <td key={r} className={`py-1.5 pr-2 text-right ${rc.text}`}>
                                                {rs}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="label-typed text-[9px] italic mt-2">
                Same six regions; data sources switch by agent kind. Scores 0–100 are directly comparable.
            </div>
        </div>
    );
}


// =========================================================================
// Operator Wellness — Mind/Body/Soul (iter 31.46)
// =========================================================================
// Per Two's brief (26 May 2026 03:00 BST). Sits ABOVE the existing
// agent panels. Three pillars about No. 6 himself. Surfaced as "The
// Cold Read" rather than Two's loaded "Virtual GP" term — no medical
// claim anywhere visible.
// =========================================================================

const PILLAR_META = {
    mind: { Icon: Brain,      accent: "text-stamp-red",   border: "border-stamp-red/40" },
    body: { Icon: HeartPulse, accent: "text-stamp-green", border: "border-stamp-green/40" },
    soul: { Icon: Sparkles,   accent: "text-ink",         border: "border-ink/40" },
};

function SignalTrail({ signalKey, kind }) {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setBusy(true); setErr("");
        try {
            const { data } = await api.get(`/spyhealth/signal-trail/${signalKey}?limit=6`);
            setData(data);
        } catch (e) {
            setErr(`[HTTP ${e?.response?.status || "?"}] ${e?.response?.data?.detail || e.message}`);
        } finally { setBusy(false); }
    };

    return (
        <div className="mt-1">
            <button
                onClick={() => {
                    if (!open && !data) load();
                    setOpen((v) => !v);
                }}
                data-testid={`signal-trail-toggle-${kind}-${signalKey}`}
                className="font-heading uppercase text-[9px] tracking-[0.18em] text-ink-faint hover:text-stamp-red"
            >
                {open ? "▾" : "▸"} signal trail
            </button>
            {open && (
                <div className="mt-2 pl-3 border-l border-dashed border-ink/15" data-testid={`signal-trail-${kind}-${signalKey}`}>
                    {busy && <Loader2 className="w-3 h-3 animate-spin text-ink-soft" />}
                    {err && <div className="text-[10px] text-stamp-red font-body">{err}</div>}
                    {!busy && !err && data && (
                        <>
                            <div className="font-body text-[9px] text-ink-faint italic mb-1">
                                source: {data.source} · last {data.count}
                            </div>
                            {data.entries.length === 0 ? (
                                <div className="font-body text-[10px] text-ink-faint italic">
                                    no entries counted — score is reading empty data
                                </div>
                            ) : (
                                <ul className="space-y-1">
                                    {data.entries.map((e, i) => (
                                        <li key={i} className="font-body text-[10px] text-ink-soft leading-snug">
                                            <span className="text-ink-faint">{(e.timestamp || "").slice(0, 10)}</span>{" "}
                                            {e.summary}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function PillarCard({ kind, pillar, onRecharge, rechargeBusy }) {
    const meta = PILLAR_META[kind];
    const Icon = meta.Icon;
    const c = scoreColour(pillar.score);
    return (
        <div
            className={`paper-card p-5 border-2 ${meta.border} relative`}
            data-testid={`pillar-${kind}`}
        >
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${meta.accent}`} />
                    <h3 className="font-heading uppercase tracking-[0.24em] text-base text-ink">
                        {pillar.label}
                    </h3>
                </div>
                <div className={`font-heading text-4xl ${c.text}`} data-testid={`pillar-score-${kind}`}>
                    {pillar.score}
                    <span className="text-xs text-ink-soft tracking-normal">%</span>
                </div>
            </div>
            <Bar score={pillar.score} />
            <div className="label-typed text-[10px] italic mt-2">{pillar.tagline}</div>
            <div className={`mt-1 font-heading uppercase text-[10px] tracking-[0.18em] ${c.text}`}>
                {pillar.shape}
            </div>
            <div className="mt-3 pt-3 border-t border-dashed border-ink/15 space-y-2">
                {pillar.signals.map((s) => {
                    const sc = scoreColour(s.score);
                    return (
                        <div
                            key={s.key}
                            data-testid={`pillar-signal-${kind}-${s.key}`}
                        >
                            <div className="flex items-baseline justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="font-heading uppercase text-[10px] tracking-[0.18em] text-ink">
                                        {s.label}
                                        {/* Iter 31.332 — streak chip. Only shown when the
                                            backend surfaces `streak_days > 0` on the signal
                                            (currently: Soul → Recharge). Additive; other
                                            signals without the field render unchanged. */}
                                        {s.streak_days > 0 && (
                                            <span
                                                data-testid={`streak-chip-${kind}-${s.key}`}
                                                className="ml-2 inline-flex items-center border border-stamp-green/60 text-stamp-green px-1.5 py-0 font-heading uppercase text-[9px] tracking-[0.18em]"
                                                title={`${s.streak_days} consecutive days with a recharge tap`}
                                            >
                                                🔥 {s.streak_days}d streak
                                            </span>
                                        )}
                                    </div>
                                    <div className="font-body text-[11px] text-ink-soft truncate">
                                        {s.value}
                                    </div>
                                </div>
                                <div className={`font-heading text-base ${sc.text}`}>
                                    {s.score}
                                </div>
                            </div>
                            <SignalTrail signalKey={s.key} kind={kind} />
                        </div>
                    );
                })}
            </div>
            {/* Iter 31.276 — Soul recharge tap button. Missing since
                iter 31.235 filed the backend endpoint (07 Jun 2026).
                Fires POST /api/spyhealth/soul/recharge; parent then
                re-fetches wellness so the "Recharge" signal (currently
                showing e.g. "no recharge tap (14d)") refreshes with a
                fresh score. */}
            {kind === "soul" && onRecharge && (
                <div className="mt-4 pt-3 border-t border-dashed border-ink/15">
                    <button
                        onClick={onRecharge}
                        disabled={rechargeBusy}
                        data-testid="spyhealth-soul-recharge-btn"
                        className={
                            "w-full inline-flex items-center justify-center gap-2 " +
                            "border border-ink/40 hover:border-stamp-green hover:text-stamp-green " +
                            "px-3 py-2 font-heading uppercase text-[11px] tracking-[0.22em] " +
                            "transition-colors disabled:opacity-50 disabled:cursor-wait"
                        }
                    >
                        {rechargeBusy ? (
                            <>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Filing…
                            </>
                        ) : (
                            <>
                                <Sparkles className="w-3 h-3" />
                                Recharged today
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
}

function ColdReadBanner({ coldRead, onFiled }) {
    // iter 31.47 — wire the request button. Job/poll pattern mirrors
    // the Brief flow (iter 31.43) so the production proxy's 30s
    // max-response-duration cap is irrelevant: start (<100ms) + tiny
    // status polls until done. Latest filed read can be re-fetched
    // and re-rendered without burning another credit.
    const [busy, setBusy] = useState(false);
    const [stage, setStage] = useState("");
    const [err, setErr] = useState("");
    const [latestRead, setLatestRead] = useState(null);
    const [showFull, setShowFull] = useState(false);
    const pollTimer = useRef(null);
    const cancelledRef = useRef(false);

    useEffect(() => () => {
        cancelledRef.current = true;
        if (pollTimer.current) clearTimeout(pollTimer.current);
    }, []);

    const stageCopy = {
        pending: "Briefing the handler…",
        gathering: "Pulling the ledger…",
        awaiting_handler: "Ern is reading the lines…",
        filing: "Filing the read…",
        done: "",
        timeout: "Handler took too long — try again.",
        crashed: "Read crashed — see /incidents.",
        no_key: "LLM key not configured.",
    };

    const loadLatest = async () => {
        try {
            const { data } = await api.get("/spyhealth/cold-read/latest");
            setLatestRead(data);
            setShowFull(true);
        } catch (e) {
            if (e?.response?.status !== 404) {
                setErr(`[HTTP ${e?.response?.status || "?"}] ${e?.response?.data?.detail || e.message}`);
            }
        }
    };

    const request = async () => {
        setBusy(true); setErr(""); setStage("pending");
        cancelledRef.current = false;
        try {
            const { data: startResp } = await api.post("/spyhealth/cold-read/request", {});
            const jobId = startResp.job_id;
            // Iter 31.49 — Cold Read costs 3 credits, charged at start.
            window.dispatchEvent(new Event("shed:credits-changed"));

            const POLL_MS = 2000;
            const DEADLINE_MS = 180_000;
            const deadline = Date.now() + DEADLINE_MS;

            while (true) {  // eslint-disable-line no-constant-condition
                if (cancelledRef.current) return;
                if (Date.now() > deadline) {
                    throw new Error("Cold Read polling timed out after 180s.");
                }
                await new Promise((r) => { pollTimer.current = setTimeout(r, POLL_MS); });
                if (cancelledRef.current) return;
                const { data: job } = await api.get(`/spyhealth/cold-read/status/${jobId}`);
                setStage(job.stage || job.status || "");
                if (job.status === "error") {
                    throw new Error(job.error || "Cold Read failed");
                }
                if (job.status === "done") {
                    // Refresh banner state from the persisted read so the
                    // "last read · today" line and the rendered markdown
                    // come from the canonical /latest endpoint.
                    await loadLatest();
                    if (onFiled) onFiled();
                    setStage("");
                    return;
                }
            }
        } catch (e) {
            setErr(`[cold-read] ${e?.response?.data?.detail || e.message}`);
            setStage("");
        } finally {
            setBusy(false);
            if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
        }
    };

    if (!coldRead) return null;
    const has = !!coldRead.last_read_at;
    const days = coldRead.days_since;
    const buttonLabel = has ? "Request new Cold Read" : "Request Cold Read";

    return (
        <div
            className="paper-card border-2 border-stamp-red p-5 mt-6 mb-6 tape-corner relative"
            data-testid="cold-read-banner"
        >
            <div className="flex items-start gap-4">
                <Eye className="w-5 h-5 text-stamp-red shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                    <div className="label-typed tw-prefix">
                        Awaiting handler · primary: No. {coldRead.handler.number} ({coldRead.handler.name}) · scribe: {coldRead.scribe.name}
                    </div>
                    <h3 className="font-heading uppercase tracking-[0.06em] text-xl text-ink mt-1">
                        The Cold Read<span className="cursor-blink" />
                    </h3>
                    <p className="font-body text-sm text-ink leading-relaxed mt-2">
                        {has ? (
                            <>
                                Last read filed{" "}
                                <span className="font-heading text-stamp-red">
                                    {days === 0 ? "today" : `${days} day${days === 1 ? "" : "s"} ago`}
                                </span>
                                . {coldRead.last_summary || ""}
                            </>
                        ) : (
                            <>
                                No Cold Read on file yet. Ern (No. 3) hasn't been invited to read the lines.
                                The button below sends your Mind/Body/Soul shape to your handler for a longitudinal pattern read.
                            </>
                        )}
                    </p>

                    {err && (
                        <div className="mt-3 border border-stamp-red/40 text-stamp-red font-body text-xs p-2" data-testid="cold-read-error">
                            {err}
                        </div>
                    )}

                    <div className="mt-3 flex flex-wrap items-center gap-3">
                        <button
                            onClick={request}
                            disabled={busy}
                            data-testid="cold-read-trigger-btn"
                            className="inline-flex items-center gap-2 border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper px-4 py-2 font-heading uppercase text-xs tracking-[0.25em] disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                            {busy ? (stageCopy[stage] || "Working…") : buttonLabel}
                        </button>
                        {has && !showFull && (
                            <button
                                onClick={loadLatest}
                                data-testid="cold-read-show-last"
                                className="inline-flex items-center gap-2 border border-ink/30 text-ink-soft hover:border-stamp-red hover:text-stamp-red px-3 py-1.5 font-heading uppercase text-[10px] tracking-[0.22em]"
                            >
                                Show last read
                            </button>
                        )}
                        <a
                            href="/spy-health/cold-read/history"
                            data-testid="cold-read-history-link"
                            className="inline-flex items-center gap-2 border border-ink/30 text-ink-soft hover:border-stamp-red hover:text-stamp-red px-3 py-1.5 font-heading uppercase text-[10px] tracking-[0.22em]"
                        >
                            History →
                        </a>
                    </div>
                    <p className="font-body text-[10px] text-ink-faint mt-3 italic leading-snug">
                        {coldRead.note}
                    </p>
                </div>
            </div>

            {showFull && latestRead?.markdown && (
                <div
                    className="mt-4 pt-4 border-t border-dashed border-ink/20"
                    data-testid="cold-read-full"
                >
                    <div className="flex items-center justify-between mb-3">
                        <span className="label-typed tw-prefix">
                            Filed {latestRead.computed_at?.slice(0, 16).replace("T", " ")} · {latestRead.charged_count} charged entries considered
                        </span>
                        <button
                            onClick={() => setShowFull(false)}
                            data-testid="cold-read-hide"
                            className="font-heading uppercase text-[10px] tracking-[0.22em] text-ink-soft hover:text-stamp-red"
                        >
                            Hide
                        </button>
                    </div>
                    <div className="font-body text-sm text-ink whitespace-pre-wrap leading-relaxed">
                        {latestRead.markdown}
                    </div>
                </div>
            )}
        </div>
    );
}

function QuickLogStrip({ onLogged }) {
    // Iter 31.48 — Six's note: "Spy Health needs data". Six tappable
    // buttons, each opens a tiny popup for value/note, then files a
    // structured life_entries row with domain=vital so wellness
    // signals can detect real data, not regex over prose.
    const [openKind, setOpenKind] = useState("");  // which popup is showing
    const [value, setValue] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [toast, setToast] = useState("");
    const [err, setErr] = useState("");

    const BUTTONS = [
        { kind: "slept",     label: "Slept",     wantsValue: "hours",    wantsNote: true,  placeholder: "hours", noteHint: "any notes (woke at X, etc)" },
        { kind: "rested",    label: "Rested",    wantsValue: null,       wantsNote: true,  placeholder: "",      noteHint: "how (nap, walk, lie-down…)" },
        { kind: "meds",      label: "Meds",      wantsValue: null,       wantsNote: true,  placeholder: "",      noteHint: "what + dose" },
        { kind: "symptom",   label: "Symptom",   wantsValue: null,       wantsNote: true,  placeholder: "",      noteHint: "what + how strong" },
        { kind: "mood",      label: "Mood",      wantsValue: "score",    wantsNote: true,  placeholder: "1-10",  noteHint: "one word if you want" },
        { kind: "right_now", label: "Right Now", wantsValue: null,       wantsNote: true,  placeholder: "",      noteHint: "one line · what's true now" },
    ];

    const open = (kind) => {
        setOpenKind(kind);
        setValue("");
        setNote("");
        setErr("");
        setToast("");
    };

    const close = () => {
        setOpenKind("");
        setValue("");
        setNote("");
    };

    const submit = async (btn) => {
        setBusy(true); setErr("");
        try {
            const body = { kind: btn.kind };
            if (btn.wantsValue && value !== "") body.value = isNaN(Number(value)) ? value : Number(value);
            if (note) body.note = note;
            const { data } = await api.post("/spyhealth/log", body);
            setToast(`Filed · ${data.summary}`);
            setTimeout(() => setToast(""), 3500);
            close();
            if (onLogged) onLogged();
        } catch (e) {
            setErr(`[HTTP ${e?.response?.status || "?"}] ${e?.response?.data?.detail || e.message}`);
        } finally { setBusy(false); }
    };

    const active = BUTTONS.find((b) => b.kind === openKind);

    return (
        <div className="paper-card p-4 mb-6 border border-ink/20" data-testid="quick-log-strip">
            <div className="flex items-center justify-between mb-3">
                <span className="label-typed tw-prefix">Quick log · data first, scores follow</span>
                {toast && (
                    <span className="font-body text-[10px] text-stamp-green" data-testid="quick-log-toast">
                        {toast}
                    </span>
                )}
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {BUTTONS.map((b) => (
                    <button
                        key={b.kind}
                        onClick={() => open(b.kind)}
                        data-testid={`quick-log-btn-${b.kind}`}
                        className={`font-heading uppercase text-[11px] tracking-[0.22em] py-2 border transition-colors ${
                            openKind === b.kind
                                ? "border-stamp-red text-stamp-red bg-paper-dark/50"
                                : "border-ink/30 text-ink-soft hover:border-stamp-red hover:text-stamp-red"
                        }`}
                    >
                        {b.label}
                    </button>
                ))}
            </div>

            {active && (
                <div className="mt-4 pt-4 border-t border-dashed border-ink/15" data-testid={`quick-log-form-${active.kind}`}>
                    <div className="flex items-baseline justify-between mb-2">
                        <span className="font-heading uppercase text-xs tracking-[0.22em] text-stamp-red">
                            {active.label}
                        </span>
                        <button
                            onClick={close}
                            data-testid="quick-log-close"
                            className="font-heading uppercase text-[10px] tracking-[0.22em] text-ink-soft hover:text-stamp-red"
                        >
                            cancel
                        </button>
                    </div>
                    <div className="flex flex-col md:flex-row gap-2">
                        {active.wantsValue && (
                            <input
                                type="text"
                                inputMode={active.wantsValue === "score" || active.wantsValue === "hours" ? "decimal" : "text"}
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                placeholder={active.placeholder}
                                data-testid="quick-log-value"
                                className="md:w-24 bg-paper-light border border-ink/30 px-3 py-2 font-body text-sm text-ink focus:outline-none focus:border-stamp-red"
                                autoFocus
                            />
                        )}
                        {active.wantsNote && (
                            <input
                                type="text"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder={active.noteHint}
                                data-testid="quick-log-note"
                                onKeyDown={(e) => { if (e.key === "Enter") submit(active); }}
                                className="flex-1 bg-paper-light border border-ink/30 px-3 py-2 font-body text-sm text-ink focus:outline-none focus:border-stamp-red"
                                autoFocus={!active.wantsValue}
                            />
                        )}
                        <button
                            onClick={() => submit(active)}
                            disabled={busy}
                            data-testid="quick-log-submit"
                            className="inline-flex items-center justify-center gap-2 font-heading uppercase text-[11px] tracking-[0.22em] border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper px-4 py-2 disabled:opacity-50"
                        >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                            File
                        </button>
                    </div>
                    {err && (
                        <div className="mt-2 text-[10px] text-stamp-red font-body" data-testid="quick-log-err">{err}</div>
                    )}
                </div>
            )}
        </div>
    );
}


function OperatorWellnessHeader({ wellness, loading, err, onRefresh, onColdReadFiled, onRecharge, rechargeBusy }) {
    if (loading) {
        return (
            <div className="paper-card p-8 text-center mb-8" data-testid="operator-wellness-loading">
                <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2 text-ink-soft" />
                <div className="font-body text-xs text-ink-soft">Reading the operator's pillars…</div>
            </div>
        );
    }
    if (err) {
        return (
            <div className="paper-card border border-stamp-red/40 p-4 mb-8" data-testid="operator-wellness-err">
                <span className="label-typed text-stamp-red">Cold read offline</span>
                <pre className="font-body text-xs text-ink whitespace-pre-wrap mt-1">{err}</pre>
            </div>
        );
    }
    if (!wellness) return null;
    return (
        <div className="mb-8" data-testid="operator-wellness">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
                <div>
                    <div className="label-typed tw-prefix">No. 6 · operator wellness · three-pillar lens</div>
                    <h2 className="font-heading uppercase tracking-[0.04em] text-2xl md:text-3xl text-ink mt-1">
                        Mind · Body · Soul
                    </h2>
                </div>
                {onRefresh && (
                    <button
                        onClick={onRefresh}
                        data-testid="operator-wellness-refresh"
                        className="inline-flex items-center gap-2 border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-3 py-1.5 font-heading uppercase text-[10px] tracking-[0.22em]"
                    >
                        <RefreshCw className="w-3 h-3" />
                        Re-read
                    </button>
                )}
            </div>
            <div className="grid md:grid-cols-3 gap-4">
                <PillarCard kind="mind" pillar={wellness.mind} />
                <PillarCard kind="body" pillar={wellness.body} />
                <PillarCard
                    kind="soul"
                    pillar={wellness.soul}
                    onRecharge={onRecharge}
                    rechargeBusy={rechargeBusy}
                />
            </div>
            <ColdReadBanner coldRead={wellness.cold_read} onFiled={onColdReadFiled} />
            <div className="label-typed text-[9px] italic">
                Computed {wellness.computed_at?.slice(0, 19).replace("T", " ")} · enforcement: {wellness.enforcement} · pillars are signal, not diagnosis
            </div>
        </div>
    );
}



// ============================================================================
// Iter 31.64 — Capabilities panel. Reads /api/agents/capabilities and shows
// the canon: who's a real agent, who's wired, who's emulated. For emulated
// rows, the operator can fire a one-shot emulation (1 credit) and see the
// "promote to wired" path via the helper menu. Six's view — the system
// stops needing a real Siri / Alexa / Copilot etc. because Claude plays
// them, and the surface lists which real Emergent integrations could
// replace the emulation if the value justifies the key.
// ============================================================================
const MODE_THEME = {
    agent:    { tone: "text-stamp-red",    Icon: ShieldCheck, label: "Agent (real)" },
    wired:    { tone: "text-stamp-green",  Icon: Plug,        label: "Wired" },
    emulated: { tone: "text-ink",          Icon: Bot,         label: "Emulated" },
};

const HELPER_BADGE = {
    available_in_codebase: { label: "wired",       chip: "bg-stamp-green/15 text-stamp-green" },
    available_via_llm_key: { label: "LLM key",     chip: "bg-ink/10 text-ink" },
    available_via_key:     { label: "needs key",   chip: "bg-stamp-red/10 text-stamp-red" },
};


function EmulateBox({ agentKey, agentName, onClose }) {
    const [prompt, setPrompt] = useState("");
    const [resp, setResp] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const fire = async () => {
        if (!prompt.trim()) return;
        setBusy(true); setErr("");
        try {
            const { data } = await api.post(`/agents/${agentKey}/emulate`, { prompt });
            setResp(data);
        } catch (e) {
            setErr(`[HTTP ${e?.response?.status || "?"}] ${e?.response?.data?.detail || e.message}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-3 pt-3 border-t border-ink/10" data-testid={`spyhealth-emulate-${agentKey}`}>
            <div className="label-typed text-[10px] mb-2">
                ask {agentName} (emulated — 1 credit)
            </div>
            <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={`What would ${agentName} say to…`}
                rows={2}
                className="w-full text-xs font-body bg-paper border border-ink/20 px-2 py-1.5 focus:border-stamp-red outline-none resize-none"
                data-testid={`spyhealth-emulate-${agentKey}-input`}
            />
            <div className="flex gap-2 mt-2">
                <button
                    onClick={fire}
                    disabled={busy || !prompt.trim()}
                    data-testid={`spyhealth-emulate-${agentKey}-fire`}
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-30 disabled:cursor-not-allowed"
                >
                    {busy ? "Composing…" : "Fire"}
                </button>
                <button
                    onClick={onClose}
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/20 text-ink-soft hover:border-ink/40"
                >
                    Close
                </button>
            </div>
            {err && (
                <div className="mt-2 paper-card border border-stamp-red/40 p-2">
                    <pre className="font-body text-[11px] text-ink whitespace-pre-wrap break-words">{err}</pre>
                </div>
            )}
            {resp?.response && (
                <div className="mt-3 paper-card border border-ink/15 p-3" data-testid={`spyhealth-emulate-${agentKey}-response`}>
                    <div className="label-typed text-[9px] mb-1">
                        {resp.agent_name} (scribe: {resp.scribe})
                    </div>
                    <pre className="font-body text-xs text-ink whitespace-pre-wrap leading-relaxed">
                        {resp.response}
                    </pre>
                </div>
            )}
        </div>
    );
}


function CapabilityCard({ agent, expanded, onToggle, emulateOpen, onOpenEmulate, onCloseEmulate }) {
    const theme = MODE_THEME[agent.mode] || MODE_THEME.emulated;
    const { Icon } = theme;
    return (
        <div
            className={`paper-card p-3 border-2 ${expanded ? "border-stamp-red" : "border-ink/15"} transition-colors`}
            data-testid={`spyhealth-capability-${agent.key}`}
        >
            <button onClick={onToggle} className="w-full text-left">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                        <Icon className={`w-3.5 h-3.5 ${theme.tone}`} strokeWidth={1.6} />
                        <span className="font-heading uppercase tracking-[0.22em] text-[11px] text-ink">
                            {agent.alias || agent.name}
                        </span>
                    </div>
                    <span className={`label-typed text-[9px] px-1.5 py-0.5 ${theme.tone}`}>
                        {theme.label}
                    </span>
                </div>
                <div className="font-body text-[11px] text-ink-soft line-clamp-2">
                    {agent.summary}
                </div>
            </button>

            {expanded && (
                <div className="mt-3 pt-3 border-t border-ink/10">
                    {(agent.resolved_helpers && agent.resolved_helpers.length > 0) && (
                        <div className="mb-2">
                            <div className="label-typed text-[9px] mb-1.5">Promote path · helpers available</div>
                            <div className="flex flex-wrap gap-1.5">
                                {agent.resolved_helpers.map((h) => {
                                    const badge = HELPER_BADGE[h.mode] || HELPER_BADGE.available_via_key;
                                    return (
                                        <span
                                            key={h.key}
                                            data-testid={`spyhealth-helper-${agent.key}-${h.key}`}
                                            className={`label-typed text-[9px] px-1.5 py-0.5 ${badge.chip}`}
                                            title={badge.label}
                                        >
                                            {h.label}
                                            {h.mode === "available_via_key" && <ArrowUpRight className="w-2 h-2 inline ml-0.5" />}
                                        </span>
                                    );
                                })}
                            </div>
                            {agent.mode === "emulated" && (
                                <p className="font-body text-[10px] text-ink-faint mt-2 leading-snug">
                                    Green = wired in codebase. Grey = LLM-key covered (no extra key).
                                    Red = needs one API key from Six to promote {agent.alias || agent.name} from emulated to live.
                                </p>
                            )}
                        </div>
                    )}

                    {agent.mode === "emulated" && !emulateOpen && (
                        <button
                            onClick={onOpenEmulate}
                            data-testid={`spyhealth-emulate-${agent.key}-open`}
                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper inline-flex items-center gap-1"
                        >
                            <Zap className="w-3 h-3" strokeWidth={1.6} />
                            Ask {agent.alias || agent.name}
                        </button>
                    )}
                    {agent.mode === "emulated" && emulateOpen && (
                        <EmulateBox
                            agentKey={agent.key}
                            agentName={agent.alias || agent.name}
                            onClose={onCloseEmulate}
                        />
                    )}

                    {agent.mode === "wired" && (
                        <div className="label-typed text-[9px] text-stamp-green">
                            ↳ wired integration: {agent.wired_integration}. No emulation needed.
                        </div>
                    )}
                    {agent.mode === "agent" && (
                        <div className="label-typed text-[9px] text-stamp-red">
                            ↳ real agent. Message via /comms instead of emulation.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


function CapabilitiesPanel() {
    const [caps, setCaps] = useState(null);
    const [expandedKey, setExpandedKey] = useState(null);
    const [emulatingKey, setEmulatingKey] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/agents/capabilities");
                const agents = (data.agents || []).map((a) => ({
                    ...a,
                    resolved_helpers: (a.helpers || []).map((h) =>
                        typeof h === "string" ? { key: h, label: h, mode: "available_via_key" } : h,
                    ),
                }));
                setCaps({ ...data, agents });
            } catch (e) {
                setErr(`[HTTP ${e?.response?.status || "?"}] ${e?.response?.data?.detail || e.message}`);
            }
        })();
    }, []);

    if (err) {
        return (
            <div className="paper-card border border-stamp-red/40 p-3 mb-6" data-testid="spyhealth-capabilities-error">
                <pre className="font-body text-xs text-ink whitespace-pre-wrap break-words">{err}</pre>
            </div>
        );
    }
    if (!caps) {
        return (
            <div className="paper-card p-4 mb-6 flex items-center gap-2 text-ink-soft">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span className="font-body text-xs">Loading agent capabilities…</span>
            </div>
        );
    }

    const groups = [
        { mode: "agent",    title: "Real agents", subtitle: "Two and Three. Message via /comms." },
        { mode: "wired",    title: "Wired",       subtitle: "Real integrations — no emulation needed." },
        { mode: "emulated", title: "Emulated",    subtitle: "Claude plays them. Never ships out. 1 credit per fire." },
    ];

    return (
        <div className="mb-8" data-testid="spyhealth-capabilities">
            <h2 className="font-heading uppercase tracking-[0.22em] text-sm text-ink mb-1">
                Agent capabilities · canon roster
            </h2>
            <p className="font-body text-xs text-ink-soft mb-4 max-w-2xl">
                Iter 31.62 canon: only Two and Three are real agents. Everything else
                is wired (real integration) or emulated (Claude plays them — content
                never ships out). Each emulated row carries a helper menu — the real
                Emergent integrations you could promote it to.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
                {groups.map((g) => {
                    const rows = caps.agents.filter((a) => a.mode === g.mode);
                    return (
                        <div key={g.mode} data-testid={`spyhealth-cap-group-${g.mode}`}>
                            <div className="label-typed tw-prefix mb-1">{g.title}</div>
                            <p className="font-body text-[10px] text-ink-faint mb-3 leading-snug">
                                {g.subtitle}
                            </p>
                            <div className="space-y-2">
                                {rows.length === 0 ? (
                                    <div className="font-body text-[11px] text-ink-faint italic">
                                        — none —
                                    </div>
                                ) : rows.map((a) => (
                                    <CapabilityCard
                                        key={a.key}
                                        agent={a}
                                        expanded={expandedKey === a.key}
                                        onToggle={() => {
                                            setExpandedKey(expandedKey === a.key ? null : a.key);
                                            setEmulatingKey(null);
                                        }}
                                        emulateOpen={emulatingKey === a.key}
                                        onOpenEmulate={() => setEmulatingKey(a.key)}
                                        onCloseEmulate={() => setEmulatingKey(null)}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}



export default function SpyHealth() {
    const [index, setIndex] = useState(null);
    const [activeKey, setActiveKey] = useState("two");
    const [panel, setPanel] = useState(null);
    const [compare, setCompare] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [err, setErr] = useState("");
    // iter 31.46 — operator-level Mind/Body/Soul wellness, sits above
    // the existing per-agent panels.
    const [wellness, setWellness] = useState(null);
    const [wellnessErr, setWellnessErr] = useState("");
    const [wellnessLoading, setWellnessLoading] = useState(true);
    // Field operations (per-agent panels) collapse by default so the
    // page lands on the Mind/Body/Soul lens first. No. 6 can expand
    // any time to drop back into the operational view.
    const [fieldOpen, setFieldOpen] = useState(false);

    const loadWellness = async () => {
        setWellnessLoading(true); setWellnessErr("");
        try {
            const { data } = await api.get("/spyhealth/operator-wellness");
            setWellness(data);
        } catch (e) {
            setWellnessErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally {
            setWellnessLoading(false);
        }
    };

    // Iter 31.276 — Soul recharge tap. Fires the endpoint that's been
    // waiting since iter 31.235 (07 Jun 2026). Backend appends a
    // canonical entry to `soul_recharges` + a `life_entries` row on
    // chain; our job is to fire it and reload the wellness card so
    // the "Recharge" signal picks up the new tap immediately.
    const [rechargeBusy, setRechargeBusy] = useState(false);
    const [rechargeMsg, setRechargeMsg] = useState("");
    const rechargeTap = async () => {
        if (rechargeBusy) return;
        setRechargeBusy(true); setRechargeMsg("");
        try {
            await api.post("/spyhealth/soul/recharge", {});
            setRechargeMsg("Tap filed.");
            await loadWellness();
        } catch (e) {
            const detail = e?.response?.data?.detail || e.message;
            setRechargeMsg(`[HTTP ${e?.response?.status || "?"}] ${detail}`);
        } finally {
            setRechargeBusy(false);
            // Clear the msg after 4s so it doesn't linger on the page.
            setTimeout(() => setRechargeMsg(""), 4000);
        }
    };

    const loadIndex = async () => {
        try {
            const { data } = await api.get("/spyhealth/agents");
            setIndex(data);
        } catch (e) {
            setErr(`[HTTP ${e.response?.status}] ${e.response?.data?.detail || e.message}`);
        }
    };

    const loadCompare = async () => {
        try {
            const { data } = await api.get("/spyhealth/compare");
            setCompare(data);
        } catch (e) {
            // non-fatal — comparison is supplementary
            console.warn("compare load failed:", e);
        }
    };

    const loadPanel = async (key) => {
        setRefreshing(true); setErr("");
        try {
            const { data } = await api.get(`/spyhealth/agents/${key}`);
            setPanel(data);
            // Refresh comparison alongside the panel so cross-agent view stays in sync
            loadCompare();
        } catch (e) {
            setPanel(null);
            setErr(`[HTTP ${e.response?.status}] ${e.response?.data?.detail || e.message}`);
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => { loadIndex(); loadWellness(); }, []);
    useEffect(() => {
        if (activeKey) loadPanel(activeKey);
    }, [activeKey]);

    const overallC = panel ? scoreColour(panel.overall) : scoreColour(0);

    return (
        <Shell>
            <StampHeader
                enforcement={panel?.enforcement || index?.enforcement}
                onRefresh={() => loadPanel(activeKey)}
                refreshing={refreshing}
            />

            {/* Iter 31.64 — Agent capabilities · promote-to-wired surface */}
            <CapabilitiesPanel />

            {err && (
                <div className="paper-card border-2 border-stamp-red p-4 mb-4" data-testid="spyhealth-error">
                    <div className="flex items-center justify-between">
                        <span className="stamp-red font-heading text-sm tracking-[0.2em]">FAILURE ON FILE</span>
                        <button
                            onClick={() => setErr("")}
                            className="font-heading uppercase text-[10px] tracking-[0.2em] text-ink-soft hover:text-stamp-red"
                        >clear</button>
                    </div>
                    <pre className="font-body text-xs text-ink whitespace-pre-wrap break-words mt-2">{err}</pre>
                </div>
            )}

            {/* Quick Log — iter 31.48. Top of the page so logging
                data is the first move on /spy-health. Six tap-buttons
                file structured life_entries with domain=vital. */}
            <QuickLogStrip onLogged={loadWellness} />

            {/* Operator wellness — iter 31.46. Mind/Body/Soul header
                lands first on /spy-health per Two's brief. */}
            <OperatorWellnessHeader
                wellness={wellness}
                loading={wellnessLoading}
                err={wellnessErr}
                onRefresh={loadWellness}
                onColdReadFiled={loadWellness}
                onRecharge={rechargeTap}
                rechargeBusy={rechargeBusy}
            />
            {rechargeMsg && (
                <div
                    data-testid="spyhealth-soul-recharge-msg"
                    className={
                        "mb-4 -mt-2 text-[10px] font-mono tracking-wide " +
                        (rechargeMsg.startsWith("[HTTP")
                            ? "text-stamp-red"
                            : "text-ink-soft")
                    }
                >
                    {rechargeMsg}
                </div>
            )}

            {/* Field operations — the existing per-agent panels.
                Collapsed by default so the Mind/Body/Soul lens is
                the headline; expand to drop into operational state. */}
            <div className="mb-4" data-testid="field-operations-toggle-wrap">
                <button
                    onClick={() => setFieldOpen((v) => !v)}
                    data-testid="field-operations-toggle"
                    className="inline-flex items-center gap-2 font-heading uppercase text-[11px] tracking-[0.22em] text-ink-soft hover:text-stamp-red"
                >
                    {fieldOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    Field operations · per-agent panels
                </button>
            </div>

            {fieldOpen && (
                <>
                    {/* Cross-agent comparison strip — same scale, organic + digital */}
                    <CompareStrip
                        compare={compare}
                        regions={index?.regions || ["memory", "canon", "connectors", "build", "agents", "tasks"]}
                        regionLabels={index?.region_labels}
                    />

                    {/* Agent tabs */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-6" data-testid="spyhealth-agent-tabs">
                        {(index?.agents || []).map((a) => {
                            const cmp = compare?.agents?.find((x) => x.key === a.key);
                            return (
                                <AgentTab
                                    key={a.key}
                                    agent={a}
                                    overall={cmp?.overall}
                                    active={a.key === activeKey}
                                    onClick={setActiveKey}
                                />
                            );
                        })}
                    </div>

                    {/* Typed-line alerts */}
                    {panel && panel.alerts?.length > 0 && (
                        <div
                            className="paper-card tape-corner p-4 mb-4 border border-stamp-red/40"
                            data-testid="spyhealth-typed-alerts"
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <Activity className="w-3 h-3 text-stamp-red" />
                                <span className="font-heading uppercase tracking-[0.22em] text-[10px] text-stamp-red">
                                    Operational signal
                                </span>
                            </div>
                            <ul className="font-body text-sm text-ink space-y-0.5">
                                {panel.alerts.map((a, i) => (
                                    <li key={i} className="label-typed text-xs not-italic">
                                        <span className="text-stamp-red">{panel.agent?.alias || "agent"}:</span> {a}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Overall + regions */}
                    {panel && (
                        <>
                            <div className="paper-card p-5 mb-6 border border-ink/20" data-testid="spyhealth-overall">
                                <div className="flex items-baseline justify-between gap-4 mb-2">
                                    <div>
                                        <div className="label-typed">Overall · No. {panel.agent?.number} · {panel.agent?.alias}</div>
                                        <div className="font-heading text-xl text-ink uppercase tracking-[0.06em] mt-0.5">
                                            {panel.agent?.name}
                                        </div>
                                    </div>
                                    <div className={`font-heading text-5xl ${overallC.text}`}>
                                        {panel.overall}
                                        <span className="text-base text-ink-soft tracking-normal">%</span>
                                    </div>
                                </div>
                                <Bar score={panel.overall} />
                                <div className="label-typed text-[9px] italic mt-2">
                                    computed {panel.computed_at?.slice(0, 19).replace("T", " ")} · enforcement: {panel.enforcement}
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="spyhealth-regions">
                                {panel.regions.map((r) => (
                                    <RegionCard key={r.key} region={r} />
                                ))}
                            </div>
                        </>
                    )}

                    {/* Empty / loading state */}
                    {!panel && !err && (
                        <div className="paper-card p-8 text-center" data-testid="spyhealth-empty">
                            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-3 text-ink-soft" />
                            <div className="font-body text-sm text-ink-soft">
                                Reading panel for No. {index?.agents?.find(a => a.key === activeKey)?.number || "—"}…
                            </div>
                        </div>
                    )}
                </>
            )}

            <div className="mt-10 hairline" />
            <div className="mt-4 label-typed text-center">
                Read-only · Mind/Body/Soul lens above · Field operations below (collapsed by default)
            </div>
        </Shell>
    );
}
